import type { ComponentPropsWithoutRef } from "react";
import {
  citationEntryKey,
  isLegacyNumericCitationToken,
  resolveCitationKeyFromHref,
  type CitationEntry,
  type CitationMarkerStyle,
} from "./citationUtils";

/**
 * ReactMarkdown wiring for inline [cite](<citation:KEY>) badges, shared by
 * every assistant surface that renders citations (staff BotMessage, the
 * Opportunity Research pane). Keep marker semantics here in sync with
 * citationUtils / lib/citationMarkers.
 */

export const citationUrlTransform = (href?: string) => {
  if (!href) return href;
  if (href.startsWith("citation:")) return href;
  if (href.startsWith("/") || href.startsWith("#")) return href;
  if (href.startsWith("http://") || href.startsWith("https://") || href.startsWith("mailto:") || href.startsWith("tel:")) {
    return href;
  }
  return undefined;
};

export const citationBadgeClassName =
  "mx-0.5 inline-flex h-5 w-5 -translate-y-1 items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 dark:border-blue-800/50 dark:bg-blue-900/30 dark:text-blue-300";

/**
 * Hover title for resolved badges: show the References entry key, not a remapped
 * legacy open token (badge/aria already use the display number).
 */
function resolvedCitationTitle(
  openToken: string,
  markerStyle: CitationMarkerStyle | undefined,
  citations: CitationEntry[] | undefined,
): string {
  if (
    markerStyle === "legacy_numeric" &&
    isLegacyNumericCitationToken(openToken) &&
    citations?.length
  ) {
    const index = Number(openToken);
    if (Number.isInteger(index) && index >= 1 && index <= citations.length) {
      const entryKey = citationEntryKey(citations[index - 1]);
      if (entryKey) return entryKey;
    }
  }
  return openToken;
}

export const buildMarkdownComponents = (
  onCitationClick: (key: string) => void,
  displayNumbers?: Map<string, number>,
  markerStyle?: CitationMarkerStyle,
  citations?: CitationEntry[],
) => ({
  a: ({ href, children, ...props }: ComponentPropsWithoutRef<"a">) => {
    if (href?.startsWith("citation:")) {
      const rawKey = href.slice("citation:".length).trim();
      const key = resolveCitationKeyFromHref(rawKey, displayNumbers?.keys());
      if (key) {
        // Always show the References list number when we can resolve it.
        // Unresolved / invented keys stay a non-interactive "?" (do not cram "cite"
        // into the fixed 20px badge or promise an open that cannot resolve).
        const displayNumber = displayNumbers?.get(key);
        if (displayNumber == null) {
          // After classic leftovers are stripped on direct answers, unresolved
          // digits are invented direct keys. "Legacy citation index" is only for
          // true legacy_numeric surfaces (e.g. stale translation indices).
          const isLegacyLeftover =
            isLegacyNumericCitationToken(key) &&
            markerStyle === "legacy_numeric";
          const unresolvedLabel = isLegacyLeftover
            ? `Legacy citation index ${key}`
            : `Unresolved citation ${key}`;
          return (
            <span
              className={`${citationBadgeClassName} cursor-default opacity-60`}
              aria-label={unresolvedLabel}
              title={key}
            >
              ?
            </span>
          );
        }

        const badgeLabel = String(displayNumber);
        const title = resolvedCitationTitle(key, markerStyle, citations);
        return (
          <button
            type="button"
            onClick={() => onCitationClick(key)}
            className={citationBadgeClassName}
            aria-label={`Open citation ${badgeLabel}`}
            title={title}
          >
            {badgeLabel}
          </button>
        );
      }
    }

    return (
      <a href={href} {...props}>
        {children}
      </a>
    );
  },
});
